import { ComponentProps } from "react";
import { SanitizedPulseData } from "@features/pulse/utils/types";
import { URL_ORIGIN } from "@features/seo/utils/constants";
import { getPublisherLogo } from "@features/seo/utils/helpers";
import { schemaType } from "@features/seo/utils/types";
import AuthorDetails from "@routes/Author/components/AuthorDetails";
import { FeedSection } from "@routes/Section/utils/types";
import { VideoCardProps } from "@routes/Videos/VideosPage/utils/types";
import {
  USFArticleSchemaProps,
  USFBreadcrumbSchemaProps,
  USFVideoSchemaProps,
} from "@sphtech/web2-seo-schema";
import {
  normalizeHref,
  replaceTextSingleQuote,
  textToSlug,
} from "@util/helpers";
import dayjs from "dayjs";
import { isEmpty, trim } from "lodash-es";

/**
 * Helper function to get the breadcrumb list for the section.
 *
 * @param entity The section content.
 * @returns {USFBreadcrumbSchemaProps["items"]}
 */
export const getBreadcrumbProperties = (
  entity: FeedSection
): USFBreadcrumbSchemaProps["items"] => {
  const breadcrumbList = [];

  // Add parent breadcrumb if it exists and is not "Home"
  if (typeof entity.parent !== "undefined") {
    breadcrumbList.push({
      position: 1,
      name: entity.parent.name,
      item: normalizeHref(entity.parent.urlPath, URL_ORIGIN),
    });
  }

  // Add the current entity breadcrumb
  breadcrumbList.push({
    position: breadcrumbList.length + 1, // Position after parent
    name: entity.name,
    item: normalizeHref(entity.urlPath, URL_ORIGIN),
  });

  return breadcrumbList;
};

/**
 * Helper function to get the video schema properties.
 *
 * @param video The video object.
 * @returns {USFVideoSchemaProps}
 */
export const getVideoSchemaProperties = (
  video: VideoCardProps
): USFVideoSchemaProps => {
  const { title, description, publishedAt, thumbnail, duration, id } = video;

  const schemaProperties = {
    name: title,
    description: description || "",
    thumbnailUrl: thumbnail,
    uploadDate: publishedAt || "",
    duration: dayjs.duration(duration).toISOString(),
    embedUrl: `${URL_ORIGIN}/videos/${textToSlug(title)}/${id}`,
  };

  return schemaProperties;
};

/**
 * Helper function to get the video schema properties.
 *
 * @param video The video object.
 * @returns {USFVideoSchemaProps}
 */
export const getArticleSchemaPropertiesForVideo = (
  video: VideoCardProps
): USFArticleSchemaProps => {
  const { title, description, publishedAt, thumbnail, id } = video;

  const urlPath = `${URL_ORIGIN}/videos/${textToSlug(title)}/${id}`;
  const schemaProperties = {
    url: urlPath,
    title: title,
    description: description || "",
    images: [{ url: thumbnail, width: 1280, height: 720 }],
    datePublished: publishedAt || "",
    dateModified: publishedAt || "",
    publisherName: "The Business Times",
    publisherLogo: getPublisherLogo(),
    speakable: {
      cssSelector: [
        "[data-testid='article-title']",
        "[data-testid='article-description']",
      ],
    },
  };

  return schemaProperties;
};

/**
 * Returns JSON-LD objects for the Global Edition page of The Business Times.
 *
 * @returns {schemaType} JSON-LD object
 */
export const getGlobalPageSchemaProperties = (): schemaType => {
  const jsonLdWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Global Edition - The Business Times",
    url: "https://www.businesstimes.com.sg/global",
    description:
      "The Business Times Global Edition - Trusted Asia business news and analysis. Get regional perspectives on Southeast Asia and Greater China's markets and economies.",
    isPartOf: {
      "@type": "WebSite",
      name: "The Business Times",
      url: "https://www.businesstimes.com.sg/",
    },
  };

  return jsonLdWebPage;
};

/**
 * Helper function to get the author JSONLD.
 *
 * @param param The profile content.
 * @returns {schemaType}
 */
export const getAuthorSchemaProperties = ({
  headshotImage,
  name,
  twitterHandle,
  bio_raw,
  urlPath,
}: ComponentProps<typeof AuthorDetails>): schemaType => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: name,
    url: urlPath,
    ...(typeof headshotImage !== "undefined" ? { image: headshotImage } : {}),
    jobTitle: "Journalist",
    ...(!isEmpty(twitterHandle)
      ? { sameAs: [`https://www.twitter.com/${twitterHandle}`] }
      : {}),
    worksFor: { "@type": "NewsMediaOrganization", name: "The Business Times" },
    description: trim(bio_raw),
    author: { "@type": "Person", name: name },
    mainEntityOfPage: { "@type": "WebPage", "@id": urlPath },
  };

  return jsonLd;
};

/**
 * Helper function to get the pulse JSONLD.
 * @param pulseArticle The pulse article object.
 * @param description The description of the article.
 * @param url The URL path of the article.
 * @returns {schemaType}
 */
export const getPulseSchemaProperties = (
  pulseArticle: SanitizedPulseData,
  description: string,
  url: string
): schemaType => {
  const { title, media, published, lastmodified, authors } = pulseArticle;

  const jsonLd: schemaType = {
    "@context": "https://schema.org",
    "@type": "Article",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${URL_ORIGIN}${url}`,
      datePublished: published,
      dateModified: lastmodified,
    },
    headline: replaceTextSingleQuote(title),
    image: media || "",
    publisher: {
      "@type": "NewsMediaOrganization",
      name: "The Business Times",
      logo: {
        "@type": "ImageObject",
        url: getPublisherLogo().url,
      },
    },
    ...(authors
      ? {
          author: {
            "@type": "Person",
            name: authors,
          },
        }
      : {}),
    description: description,
  };

  return jsonLd;
};
